/* eslint-disable no-console */
/** @ts-check */

(function () {
  "use strict";

  /* ===============================
     SHARED HELPERS
  ============================== */

  // Everything the drawer-only click guards care about lives under one of these.
  const DRAWER_SCOPE_SELECTOR = "cart-drawer-component, .cart-drawer";

  /**
   * Collapse bursts of calls (e.g. cart:update + cart:refresh + cart-drawer:open fired
   * back-to-back) into a single run after `wait` ms.
   * @param {() => void} fn
   * @param {number} wait
   */
  function debounce(fn, wait) {
    let timer = 0;
    return function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(fn, wait);
    };
  }

  /**
   * Runs one handler of a consolidated listener. An exception is re-thrown asynchronously so it
   * is still reported exactly like before, but it cannot stop the other handlers from running
   * (same isolation the separate listeners used to give).
   * @param {() => void} fn
   */
  function runIsolated(fn) {
    try {
      fn();
    } catch (error) {
      window.setTimeout(() => {
        throw error;
      });
    }
  }

  /* ===============================
     MINI CART MAIN TABS (Cart / Auctions)
  ============================== */

  /**
   * @param {HTMLElement} root
   * @param {"cart"|"auctions"} name
   */
  function setMiniCartActiveTab(root, name) {
    const tabs = Array.from(root.querySelectorAll("[data-bv-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-bv-panel]"));
    if (!tabs.length || !panels.length) return;

    tabs.forEach((btn) => {
      const isActive = btn.getAttribute("data-bv-tab") === name;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    panels.forEach((panel) => {
      const isActive = panel.getAttribute("data-bv-panel") === name;
      panel.classList.toggle("is-active", isActive);
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    if (name === "auctions") {
      const target = document.querySelector(".wishlist-replace");
      if (target) loadMinicartWishlist(target);
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function initMiniCartTabs(root) {
    const tabs = Array.from(root.querySelectorAll("[data-bv-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-bv-panel]"));
    if (!tabs.length || !panels.length) return;

    /**
     * @param {string} name
     */
    function activate(name) {
      setMiniCartActiveTab(root, /** @type {"cart"|"auctions"} */ (name));
    }

    tabs.forEach((btn) => {
      btn.addEventListener("click", () => {
        const tab = btn.getAttribute("data-bv-tab");
        if (tab === "cart" || tab === "auctions") activate(tab);
      });
    });

    activate("cart");
  }

  function bootTabs() {
    document.querySelectorAll(".bv-mini-cart").forEach(initMiniCartTabs);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootTabs);
  } else {
    bootTabs();
  }

  function resetMiniCartTabs() {
    document.querySelectorAll(".bv-mini-cart").forEach((root) => {
      if (root instanceof HTMLElement) setMiniCartActiveTab(root, "cart");
    });
  }

  let cartPriceRefreshInFlight = false;

  function getCartSectionIds() {
    const sectionIds = Array.from(document.querySelectorAll("cart-items-component[data-section-id]"))
      .map((element) => element.getAttribute("data-section-id"))
      .filter(Boolean);

    if (!sectionIds.includes("cart-drawer")) {
      sectionIds.push("cart-drawer");
    }

    return sectionIds.filter((id, index, arr) => arr.indexOf(id) === index);
  }

  async function refreshCartDrawerPricesOnOpen() {
    if (cartPriceRefreshInFlight) return;

    cartPriceRefreshInFlight = true;
    window.__BV_CART_PRICE_REFRESH_UNTIL = Date.now() + 1500;

    try {
      const cartResponse = await fetch("/cart.js", {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" }
      });

      if (!cartResponse.ok) {
        throw new Error("Cart state could not be loaded");
      }

      const cart = await cartResponse.json();
      const items = Array.isArray(cart && cart.items) ? cart.items : [];
      const sections = getCartSectionIds();
      const updates = {};

      items.forEach((item) => {
        if (item && item.key) {
          updates[item.key] = item.quantity;
        }
      });

      const updateResponse = await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          updates,
          sections: sections.join(","),
          sections_url: window.location.pathname
        })
      });

      const updatedCart = await updateResponse.json();

      if (!updateResponse.ok) {
        throw new Error(updatedCart.description || updatedCart.message || "Cart prices could not be refreshed");
      }

      document.dispatchEvent(new CustomEvent("cart:update", {
        detail: {
          data: {
            itemCount: Number(updatedCart.item_count) || 0,
            source: "cart-price-refresh",
            sections: updatedCart.sections || {}
          },
          cart: updatedCart
        }
      }));

      document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: updatedCart } }));
    } catch (error) {
      console.warn("[BV Mini Cart] Cart price refresh failed:", error);
    } finally {
      cartPriceRefreshInFlight = false;
      window.__BV_CART_PRICE_REFRESH_UNTIL = Date.now() + 300;
    }
  }

  function applyDeviceFingerpintToCart() {
    if (!window.fn_device_fingerprint_sdk) return;

    var deviceData = window?.fn_device_fingerprint_sdk?.device;

    if (deviceData) {
      const fingerprintValue = JSON.parse(deviceData);
      const attributesToAdd = {};

      if (fingerprintValue?.device?.screenHeight && fingerprintValue?.device?.screenWidth) {
        attributesToAdd.resolution = `${fingerprintValue.device.screenWidth}x${fingerprintValue.device.screenHeight}`;
      }

      if (fingerprintValue?.user_agent) {
        attributesToAdd.userAgent = fingerprintValue.user_agent;
      }

      if (fingerprintValue?.fingerprint_id) {
        attributesToAdd.fingerPrintID = fingerprintValue.fingerprint_id;
      }

      if (fingerprintValue?.device?.hasSessionStorage) {
        attributesToAdd.cookieSupport = true;
      }

      if (fingerprintValue?.device?.colorDepth) {
        attributesToAdd.colorDepth = fingerprintValue?.device?.colorDepth;
      }

      if (fingerprintValue?.device?.timezoneOffset) {
        attributesToAdd.timezoneOffset = fingerprintValue.device.timezoneOffset;
      }

      if (fingerprintValue?.device?.hasLocalStorage) {
        attributesToAdd.localstorageSupport = true;
      }

      if (fingerprintValue?.device?.screenHeight) {
        attributesToAdd.screenHeight = fingerprintValue?.device?.screenHeight;
      }

      if (fingerprintValue?.device?.screenWidth) {
        attributesToAdd.screenWidth = fingerprintValue?.device?.screenWidth;
      }

      if (!Object.keys(attributesToAdd).length) {
        return;
      }

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: attributesToAdd
        })
      }).catch(function(err) {
        console.error('Failed to save device_id to cart attributes:', err);
      });
    }
  }

  /* ===============================
     BV CART PROGRESS — UNIVERSAL (MINI + MAIN CART)
  ============================== */

  // Progress bar code has been moved to bv-cart-progress.js

  /* ===============================
     MINI CART WISHLIST
  ============================== */

 let minicartWishlistLoading = false;

 // Created once and reused; constructing Intl.NumberFormat per item is comparatively expensive.
 const euroFormatter = new Intl.NumberFormat("de-DE", {
   style: "currency",
   currency: "EUR"
 });

function getWishlistApiUrl(endpoint = "") {
  const proxy = window.WISHLIST_PROXY || "/apps/wishlist";
  const baseUrl = proxy.endsWith("/") ? proxy.slice(0, -1) : proxy;
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  return `${baseUrl}${path}`;
}

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function formatEuro(value) {
  return euroFormatter.format(Number(value || 0));
}

function renderWishlistProduct(item) {
  const variant = item.variant;

  if (!variant || !variant.id) {
    return "";
  }

  const productUrl = `/products/${item.handle}?variant=${variant.id}`;
  const title = item.title || "Untitled product";
  const image = variant.image || window.themeAssetUrls?.noImage || "/assets/icon-noimage.png";
  const price = Number(variant.price || 0);
  const compareAtPrice = Number(variant.compareAtPrice || 0);
  const showCompareAtPrice = compareAtPrice > price;

  const availableForSale = variant.availableForSale !== false && Number(variant.quantity || 0) > 0;

  return `
    <div
      class="cart-wishlist__product wishlist-product"
      data-pid="${escapeHtml(item.productId)}"
      data-product-id="${escapeHtml(item.productId)}"
      data-variant-id="${escapeHtml(variant.id)}"
    >
      <div class="wishlist-product__image">
        <a href="${productUrl}">
          <img
            class="wishlist-product__image-img"
            src="${escapeHtml(image)}"
            alt="${escapeHtml(title)}"
            title="${escapeHtml(title)}"
            loading="lazy"
          >
        </a>
      </div>

      <div class="wishlist-product__content">
        <div class="wishlist-product__header">
          <div class="wishlist-product__name">
            <a href="${productUrl}">
              ${escapeHtml(title)}
            </a>
          </div>

          <div class="wishlist-product__remove">
            <button
              type="button"
              class="js-remove-from-wishlist"
              data-variant-id="${escapeHtml(variant.id)}"
              aria-label="Remove from wishlist"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6.09091H20M9.76136 15.9432V10.1477M13.2386 15.9432V10.1477M13.2386 3H9.76136C9.45395 3 9.15913 3.12212 8.94176 3.33949C8.72439 3.55686 8.60227 3.85168 8.60227 4.15909V6.09091H14.3977V4.15909C14.3977 3.85168 14.2756 3.55686 14.0582 3.33949C13.8409 3.12212 13.546 3 13.2386 3ZM16.8009 18.9336C16.7794 19.224 16.6487 19.4955 16.435 19.6934C16.2214 19.8912 15.9407 20.0008 15.6495 20H7.35045C7.05927 20.0008 6.77859 19.8912 6.56495 19.6934C6.35132 19.4955 6.22059 19.224 6.19909 18.9336L5.125 6.09091H17.875L16.8009 18.9336Z" stroke="#191919" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="wishlist-product__prices">
          <div class="wishlist-product__price salesPrice">
            ${formatEuro(price)}
          </div>

          ${
            showCompareAtPrice
              ? `<div class="wishlist-product__price listPrice">${formatEuro(compareAtPrice)}</div>`
              : `<div class="wishlist-product__price originalPrice d-none"></div>`
          }
        </div>

        ${
          variant.title && variant.title !== "Default Title"
            ? `
              <div class="wishlist-product__attributes">
                <div class="wishlist-product__attribute">
                  Größe: ${escapeHtml(variant.title)}
                </div>
              </div>
            `
            : ""
        }

        <div class="error-message"></div>

        <div class="wishlist-product__button">
          <button
            type="button"
            class="js-add-to-cart"
            data-variant-id="${escapeHtml(variant.id)}"
            ${
              Number(variant.quantity) > 0
                ? `data-quantity-left="${escapeHtml(String(Number(variant.quantity)))}"`
                : ""
            }
            ${availableForSale ? "" : "disabled"}
          >
            <span>${availableForSale ? "In den Warenkorb" : "Ausverkauft"}</span>
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderWishlistShell(itemsHtml, isEmpty = false) {
  return `
    <div class="cart-wishlist js-wishlist-container">
      <div class="cart-wishlist__messages" style="display:none;">
        <div class="cart-wishlist__messages-text">
          <span>Dieser Artikel wurde Ihrem Warenkorb hinzugefügt.</span>
        </div>
      </div>

      <div class="cart-wishlist__products" ${isEmpty ? 'style="display:none;"' : ""}>
        ${itemsHtml}
      </div>

      <div class="cart-wishlist__empty empty" ${isEmpty ? "" : 'style="display:none;"'}>
        <div class="minicart-text-container">
          Sieht aus, als wäre Ihre Wunschliste leer -
          <a href="/collections/all" class="link bold">
            finden Sie etwas, das Sie lieben werden, aus unseren Top-Tipps.
          </a>
        </div>
      </div>
    </div>
  `;
}

async function loadMinicartWishlist(target) {
  if (minicartWishlistLoading) return;

  // Never load wishlist for guests
  const injector = document.querySelector('[data-wishlist-injector]');
  if (!injector || !injector.getAttribute('data-customer-email')) return;

  minicartWishlistLoading = true;

  target.innerHTML = `
    <div class="cart-wishlist">
      <div class="cart-wishlist__products">
        Loading...
      </div>
    </div>
  `;

  try {
    const url = `${getWishlistApiUrl("v1/list")}?page=1&limit=20&moreInfo=true`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "x-client-type": "web"
      },
      credentials: "same-origin"
    });

    if (!response.ok) {
      throw new Error(`Wishlist request failed: ${response.status}`);
    }

    const result = await response.json();
    const items = result?.data?.items || [];

    if (!items.length) {
      target.innerHTML = renderWishlistShell("", true);
      return;
    }

    const itemsHtml = items.map(renderWishlistProduct).join("");
    target.innerHTML = renderWishlistShell(itemsHtml, false);

    attachMinicartWishlistRemoveHandlers(target);
    attachMinicartWishlistAddToCartHandlers(target);
  } catch (error) {
    console.error("[Minicart Wishlist] Failed to load wishlist:", error);

    target.innerHTML = `
      <div class="cart-wishlist">
        <div class="cart-wishlist__products">
          Wishlist could not be loaded.
        </div>
      </div>
    `;
  } finally {
    minicartWishlistLoading = false;
  }
}

function attachMinicartWishlistRemoveHandlers(container) {
  container.querySelectorAll(".js-remove-from-wishlist").forEach((button) => {
    button.addEventListener("click", async function (event) {
      event.preventDefault();
      event.stopPropagation();

      const variantId = this.dataset.variantId;

      if (!variantId) return;

      this.disabled = true;

      try {
        const response = await fetch(getWishlistApiUrl(`v1/${variantId}`), {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "x-client-type": "web"
          },
          credentials: "same-origin"
        });

        if (!response.ok) {
          throw new Error(`Remove failed: ${response.status}`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || "Failed to remove wishlist item");
        }

        document.dispatchEvent(new CustomEvent("wishlist:updated", {
          detail: {
            action: "removed",
            variantId
          }
        }));

        const target = document.querySelector(".wishlist-replace");

        if (target) {
          await loadMinicartWishlist(target);
        }
      } catch (error) {
        console.error("[Minicart Wishlist] Remove failed:", error);
        this.disabled = false;
      }
    });
  });
}

function attachMinicartWishlistAddToCartHandlers(container) {
  container.querySelectorAll(".js-add-to-cart").forEach((button) => {
    button.addEventListener("click", async function (event) {
      event.preventDefault();
      event.stopPropagation();

      const variantId = this.dataset.variantId;

      if (!variantId) return;

      this.disabled = true;

      const minicartComponent = document.querySelector(".cart-items-component");
      const minicartItems = minicartComponent?.querySelectorAll(`.cart-items__wrapper .mini-cart-item[data-variant-id='${variantId}']`);

      // Stock limit for this variant. When the variant is already in the mini cart we read it from
      // the row (data-quantity-left); otherwise there is no row yet, so fall back to the quantity
      // the wishlist API reported for this button. Without that fallback the very first add was
      // never validated at all — the guard below used to sit inside `if (minicartItems?.length)`.
      let quantityLeft = this.getAttribute("data-quantity-left");
      let itemTotalQuantity = 0;

      minicartItems?.forEach((minicartItem) => {
        const rowQuantityLeft = minicartItem.getAttribute("data-quantity-left");
        if (rowQuantityLeft) quantityLeft = rowQuantityLeft;

        const minicartItemAddedQuantity = minicartItem.getAttribute("data-item-quantity");

        if (minicartItemAddedQuantity && +minicartItemAddedQuantity) {
          itemTotalQuantity += +minicartItemAddedQuantity;
        }
      });

      // No limit published for this variant -> behave exactly as before and let the add through.
      if (+quantityLeft > 0 && itemTotalQuantity + 1 > +quantityLeft) {
        const wishlistItemComponent = this.closest(".wishlist-product__content");
        const errorMessageComponent = wishlistItemComponent?.querySelector(".error-message");
        const errorMessageText =
          document.querySelector(".wishlist-replace")?.getAttribute("data-exceeded-item-quantity-error") || "";

        if (errorMessageComponent) {
          if (errorMessageText) {
            errorMessageComponent.innerHTML = errorMessageText;
          }

          errorMessageComponent.classList.add("shown");

          setTimeout(() => {
            errorMessageComponent.classList.remove("shown");
          }, 1000);
        }

        this.disabled = false;

        return;
      }

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1
              }
            ]
          })
        });

        if (!response.ok) {
          throw new Error(`Add to cart failed: ${response.status}`);
        }

        const wishlist = this.closest(".cart-wishlist");
        const message = wishlist?.querySelector(".cart-wishlist__messages");

        if (message) {
          message.style.display = "block";
        }

        document.dispatchEvent(new CustomEvent("cart:update"));
        document.dispatchEvent(new CustomEvent("cart:updated"));
      } catch (error) {
        console.error("[Minicart Wishlist] Add to cart failed:", error);
        this.disabled = false;
      }
    });
  });
}

  /* ===============================
     CHECKOUT GUARD — OUT OF STOCK (MINI CART)
  ============================== */

  /**
   * @param {Element} el
   * @returns {HTMLElement | null}
   */
  function closestCartDrawer(el) {
    const drawer = el.closest("cart-drawer-component");
    return drawer instanceof HTMLElement ? drawer : null;
  }

  /**
   * @param {HTMLElement} drawer
   * @returns {HTMLElement | null}
   */
  function findCheckoutErrorEl(drawer) {
    const el = drawer.querySelector(".cart__checkout-error");
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @param {HTMLElement} drawer
   * @returns {HTMLElement | null}
   */
  function findFirstOutOfStockItem(drawer) {
    const item = drawer.querySelector(".mini-cart-item.out-of-stock");
    return item instanceof HTMLElement ? item : null;
  }

  /**
   * @param {HTMLElement} drawer
   * @returns {Boolean}
   */
  function isCustomerBlocked(drawer) {
    const checkoutButton = drawer?.querySelector(".cart__checkout-button");
    const isBlocked = checkoutButton?.getAttribute("data-is-customer-blocked") === "true";

    return isBlocked;
  }

  /**
   * @param {HTMLElement} drawer
   * @param {string} message
   */
  function showCheckoutError(drawer, message) {
    const errorEl = findCheckoutErrorEl(drawer);
    if (!errorEl) {
      window.alert(message);
      return;
    }

    errorEl.textContent = message;
    errorEl.classList.remove("hidden");
  }

  /**
   * @param {HTMLElement} drawer
   */
  function maybeHideCheckoutError(drawer) {
    const errorEl = findCheckoutErrorEl(drawer);
    if (!errorEl) return;

    if (!findFirstOutOfStockItem(drawer) && !isCustomerBlocked(drawer)) {
      errorEl.classList.add("hidden");
      errorEl.textContent = "";
    }
  }

  /**
   * @param {HTMLElement} drawer
   * @param {HTMLElement} checkoutButton
   * @returns {boolean} true if checkout may proceed
   */
  function guardCheckoutIfOutOfStock(drawer, checkoutButton) {
    const outOfStockItem = findFirstOutOfStockItem(drawer);
    const isCustomerProfileBlocked = isCustomerBlocked(drawer);

    if (!outOfStockItem && !isCustomerProfileBlocked) return true;

    let message =
      checkoutButton.getAttribute("data-oos-checkout-message") ||
      "Bitte entfernen Sie nicht verfuegbare Artikel aus dem Warenkorb, um zur Kasse zu gehen.";

    if (isCustomerProfileBlocked) {
      message = checkoutButton.getAttribute("data-customer-blocked-message") || "";
    }

    showCheckoutError(drawer, message);

    if (outOfStockItem) {
      outOfStockItem.classList.add("bv-oos-highlight");
      outOfStockItem.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => outOfStockItem.classList.remove("bv-oos-highlight"), 1800);
    }

    return false;
  }

  /**
   * @param {MouseEvent} event
   * @param {Element} target
   * @returns {boolean} true if checkout was blocked (event fully handled)
   */
  function handleCheckoutGuardClick(event, target) {
    const button = target.closest('button[name="checkout"], button#checkout, .cart__checkout-button');

    if (!(button instanceof HTMLElement)) return false;

    const drawer = closestCartDrawer(button);
    if (!drawer) return false;

    if (guardCheckoutIfOutOfStock(drawer, button)) return false;

    event.preventDefault();
    event.stopPropagation();
    // @ts-ignore
    event.stopImmediatePropagation?.();
    return true;
  }

  /* ===============================
     MAX QUANTITY VALIDATION — MINI CART
  ============================== */

  /**
   * Get max quantity for a cart item
   * @param {HTMLElement} quantitySelector - The quantity selector component
   * @returns {number | null}
   */
  function getMaxQuantityForSelector(quantitySelector) {
    if (!(quantitySelector instanceof HTMLElement)) return null;

    const input = quantitySelector.querySelector('input[name="updates[]"]');
    if (!(input instanceof HTMLInputElement)) return null;

    const inventoryQty = parseInt(input.dataset.inventoryQuantity || '0', 10);
    const inventoryPolicy = input.dataset.inventoryPolicy;
    const maxAttr = input.getAttribute('max');
    const maxFromAttr = maxAttr ? parseInt(maxAttr, 10) : null;

    // Use the max attribute from the input
    let effectiveMax = maxFromAttr;

    // If inventory is managed with deny policy, use inventory_quantity as the limit
    if (inventoryPolicy === 'deny' && inventoryQty > 0) {
      if (effectiveMax === null) {
        effectiveMax = inventoryQty;
      } else {
        effectiveMax = Math.min(effectiveMax, inventoryQty);
      }
    }

    return effectiveMax;
  }

  /**
   * Show max quantity message below quantity selector
   * @param {HTMLElement} quantitySelector - The quantity selector component
   * @param {string} message - The message to show
   */
  function showMaxQtyMessageMiniCart(quantitySelector, message) {
    // The message element is a sibling of the quantity selector, inside cart-items__quantity-controls
    // Try multiple approaches to find it
    let messageEl = null;

    // Method 1: Look in the parent container (cart-items__quantity-controls)
    const parentContainer = quantitySelector.closest('.cart-items__quantity-controls');
    if (parentContainer) {
      messageEl = parentContainer.querySelector('[data-max-qty-message]');
    }

    // Method 2: Look for next sibling element
    if (!messageEl) {
      let sibling = quantitySelector.nextElementSibling;
      while (sibling) {
        if (sibling instanceof HTMLElement && sibling.hasAttribute('data-max-qty-message')) {
          messageEl = sibling;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
    }

    // Method 3: Look in the cart item container
    if (!messageEl) {
      const cartItem = quantitySelector.closest('.cart-item, .bv-cart-item');
      if (cartItem) {
        messageEl = cartItem.querySelector('[data-max-qty-message]');
      }
    }

    if (messageEl instanceof HTMLElement) {
      messageEl.textContent = message;
      messageEl.style.display = 'block';
      messageEl.classList.add('bv-cart-item__max-qty-message--active');

      // Hide message after 5 seconds
      setTimeout(() => {
        if (messageEl instanceof HTMLElement) {
          messageEl.style.display = 'none';
          messageEl.classList.remove('bv-cart-item__max-qty-message--active');
        }
      }, 5000);
    }
  }

  /**
   * Get max quantity message from settings
   */
  function getMaxQtyMessage() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer instanceof HTMLElement && cartDrawer.dataset.maxQtyMessage) {
      return cartDrawer.dataset.maxQtyMessage;
    }
    // Fallback: try to get from cart page element (if available)
    const cartPage = document.querySelector('.bv-cart-page');
    if (cartPage instanceof HTMLElement && cartPage.dataset.maxQtyMessage) {
      return cartPage.dataset.maxQtyMessage;
    }
    return 'Maximum allowed quantity reached';
  }

  // Get ThemeEvents from global scope
  // @ts-ignore
  const ThemeEvents = window.ThemeEvents || (window.Theme && window.Theme.events);
  // @ts-ignore
  const QuantitySelectorUpdateEvent = window.QuantitySelectorUpdateEvent || (window.Theme && window.Theme.events && window.Theme.events.QuantitySelectorUpdateEvent);

  const quantitySelectorUpdateEventName = ThemeEvents?.quantitySelectorUpdate || 'quantity-selector:update';

  /**
   * Intercepts +/- clicks in the drawer before the quantity selector processes them.
   * @param {MouseEvent} e
   */
  function handleQuantityGuardClick(e) {
    if (!(e.target instanceof HTMLElement)) return;

    // Check if it's a quantity button in the cart drawer
    const button = e.target.closest('button[name="minus"], button[name="plus"]');
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const quantitySelector = button.closest('quantity-selector-component') ||
                             button.closest('cart-quantity-selector-component') ||
                             button.closest('.quantity-selector');

    if (!quantitySelector || !(quantitySelector instanceof HTMLElement)) return;

    // Only handle in mini cart (cart drawer)
    const cartDrawer = quantitySelector.closest('.cart-drawer');
    if (!cartDrawer) return;

    const input = quantitySelector.querySelector('input[name="updates[]"]');
    if (!(input instanceof HTMLInputElement)) return;

    const currentQuantity = parseInt(input.value, 10) || 1;
    const maxQty = getMaxQuantityForSelector(quantitySelector);
    const isPlusButton = button.name === 'plus';

    // Check if trying to increase beyond max
    if (isPlusButton && maxQty !== null && currentQuantity >= maxQty) {
      e.preventDefault();
      e.stopPropagation();

      const message = getMaxQtyMessage();
      showMaxQtyMessageMiniCart(quantitySelector, message);
      return;
    }

    // If not at max, let the event continue to the component
    // Also ensure button states are updated after the component processes the click
    setTimeout(() => {
      const component = /** @type {any} */ (quantitySelector);
      if (component && typeof component.updateButtonStates === 'function') {
        component.updateButtonStates();
      }
    }, 50);
  }

  /**
   * Single capture-phase click listener for the drawer (checkout guard + max-qty guard).
   * Replaces two separate document capture listeners. Clicks outside the drawer exit after
   * one closest() call. Delegation stays on document on purpose: the drawer markup can be
   * replaced via innerHTML (see draft-product fallback), which would drop listeners bound
   * directly on the drawer element.
   * @param {MouseEvent} event
   */
  function onDrawerClickCapture(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(DRAWER_SCOPE_SELECTOR)) return;

    if (handleCheckoutGuardClick(event, target)) return;
    handleQuantityGuardClick(event);
  }

  document.addEventListener("click", onDrawerClickCapture, true);

  // Also listen to quantity selector update events for input changes
  document.addEventListener(quantitySelectorUpdateEventName, (e) => {
    // Handle both QuantitySelectorUpdateEvent and CustomEvent
    if (!(e instanceof Event)) return;

    // Get the quantity from event detail (if it's a CustomEvent)
    let quantity = undefined;
    if (e instanceof CustomEvent && e.detail) {
      quantity = e.detail.quantity;
    }

    // Find the quantity selector component (event target or closest)
    let quantitySelector = null;
    if (e.target instanceof HTMLElement) {
      // Try to find the quantity-selector-component
      quantitySelector = e.target.closest('quantity-selector-component') ||
                         e.target.closest('cart-quantity-selector-component') ||
                         e.target.closest('.quantity-selector');
    }

    if (!quantitySelector || !(quantitySelector instanceof HTMLElement)) return;

    // Only handle updates in mini cart (cart drawer)
    const cartDrawer = quantitySelector.closest('.cart-drawer');
    if (!cartDrawer) return;

    const input = quantitySelector.querySelector('input[name="updates[]"]');
    if (!(input instanceof HTMLInputElement)) return;

    // Use quantity from event if available, otherwise get from input
    const currentQuantity = quantity !== undefined ? quantity : (parseInt(input.value, 10) || 1);
    const maxQty = getMaxQuantityForSelector(quantitySelector);

    // Ensure button states are updated after quantity change
    const component = /** @type {any} */ (quantitySelector);
    if (component && typeof component.updateButtonStates === 'function') {
      component.updateButtonStates();
    }

    // Check if the quantity equals max (meaning user tried to exceed but was clamped)
    const inputMax = input.getAttribute('max');
    const inputMaxQty = inputMax ? parseInt(inputMax, 10) : null;

    // If current quantity equals max and there's a max limit, show message
    if (maxQty !== null && currentQuantity === maxQty && inputMaxQty === maxQty) {
      const previousValue = input.dataset.previousValue;
      if (previousValue && parseInt(previousValue, 10) < maxQty) {
        // User tried to increase beyond max
        const message = getMaxQtyMessage();
        showMaxQtyMessageMiniCart(quantitySelector, message);
      }
    }

    // Store current value for next comparison
    input.dataset.previousValue = String(currentQuantity);

    // Hide max qty message if quantity is below max
    if (maxQty === null || currentQuantity < maxQty) {
      const messageEl = quantitySelector.parentElement?.querySelector('[data-max-qty-message]');
      if (messageEl instanceof HTMLElement) {
        messageEl.style.display = 'none';
        messageEl.classList.remove('bv-cart-item__max-qty-message--active');
      }
    }
  }, true); // Use capture phase to catch event early

  function updateDrawerQuantityButtonStates() {
    const quantitySelectors = document.querySelectorAll('.cart-drawer quantity-selector-component, .cart-drawer cart-quantity-selector-component');
    quantitySelectors.forEach((selector) => {
      if (selector instanceof HTMLElement) {
        const component = /** @type {any} */ (selector);
        if (component && typeof component.updateButtonStates === 'function') {
          component.updateButtonStates();
        }
      }
    });
  }

  /* ===============================
     CART IMAGE PLACEHOLDER HANDLING
  ============================== */

  /**
   * Handle image load errors by replacing with placeholder
   * @param {HTMLImageElement} img - The image element that failed to load
   */
  function handleImageError(img) {
    if (!(img instanceof HTMLImageElement)) return;

    // Don't replace if already showing placeholder
    if (img.src.includes('no-image')) return;

    // Get placeholder URLs from data attributes or construct from asset path
    const placeholderSmall = img.dataset.placeholderSmall || '/assets/no-image-small.jpg';
    const placeholderLarge = img.dataset.placeholderLarge || '/assets/no-image-large.jpg';

    // Determine which placeholder to use based on viewport
    const isMobile = window.innerWidth <= 749;
    const placeholderSrc = isMobile ? placeholderSmall : placeholderLarge;

    // Replace with placeholder
    img.src = placeholderSrc;
    img.alt = img.alt || 'Product image not available';

    // Prevent infinite loop if placeholder also fails
    img.onerror = null;
  }

  /**
   * Initialize image error handlers for cart images
   */
  function initCartImageErrorHandlers() {
    // Handle existing images
    const cartImages = document.querySelectorAll('.cart-items__media-image, .bv-cart-item__image img');
    cartImages.forEach((img) => {
      if (img instanceof HTMLImageElement) {
        // Only add handler if image hasn't loaded yet or if it might fail
        if (!img.complete || img.naturalHeight === 0) {
          img.addEventListener('error', () => handleImageError(img), { once: true });
        }
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCartImageErrorHandlers);
  } else {
    initCartImageErrorHandlers();
  }

  // Use MutationObserver to catch dynamically added images
  const cartImageObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const images = node.querySelectorAll ? node.querySelectorAll('.cart-items__media-image, .bv-cart-item__image img') : [];
            images.forEach((img) => {
              if (img instanceof HTMLImageElement) {
                img.addEventListener('error', () => handleImageError(img), { once: true });
              }
            });

            // Also check if the node itself is an image
            if (node instanceof HTMLImageElement &&
                (node.classList.contains('cart-items__media-image') ||
                 node.closest('.bv-cart-item__image'))) {
              node.addEventListener('error', () => handleImageError(node), { once: true });
            }
          }
        });
      }
    });
  });

  // Observe cart drawer and cart page for image additions
  function setupCartImageObserver() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    const cartPage = document.querySelector('.bv-cart-page');

    if (cartDrawer instanceof HTMLElement) {
      cartImageObserver.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }

    if (cartPage instanceof HTMLElement) {
      cartImageObserver.observe(cartPage, {
        childList: true,
        subtree: true
      });
    }
  }

  // Set up observer when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupCartImageObserver);
  } else {
    setupCartImageObserver();
  }

  /* ===============================
     REMOVE DRAFT PRODUCTS FROM CART
  ============================== */

  /**
   * Remove draft products from cart
   * Draft products should not be visible on live website
   */
  async function removeDraftProductsFromCart() {
    // Find all draft product items
    const draftItems = document.querySelectorAll('[data-is-draft="true"]');
    
    if (draftItems.length === 0) {
      return; // No draft products found
    }

    // Get section ID for cart updates
    const cartItemsComponent = document.querySelector('cart-items-component');
    const sectionId = (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.sectionId) || 'main-cart';
    
    // Collect all line numbers to remove
    /** @type {number[]} */
    const linesToRemove = [];
    draftItems.forEach((item) => {
      if (item instanceof HTMLElement) {
        const lineNumber = parseInt(item.dataset.line || '0', 10);
        if (lineNumber > 0) {
          linesToRemove.push(lineNumber);
        }
      }
    });

    if (linesToRemove.length === 0) {
      return; // No valid line numbers found
    }

    // Remove draft products one by one (Shopify API requires individual calls)
    // Process in reverse order to avoid line number shifting issues
    const sortedLines = [...linesToRemove].sort((a, b) => b - a);
    
    for (const line of sortedLines) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: line,
            quantity: 0,
            sections: sectionId,
            sections_url: window.location.pathname,
          })
        });

        if (!response.ok) {
          console.warn('[BV Cart] Failed to remove draft product at line', line);
          continue;
        }

        const responseText = await response.text();
        const parsedResponse = JSON.parse(responseText);

        // Update cart section if response includes sections
        if (parsedResponse.sections && parsedResponse.sections[sectionId]) {
          // Use morphSection if available, otherwise fallback to innerHTML
          // @ts-ignore
          if (window.morphSection || (window.Theme && window.Theme.morphSection)) {
            // @ts-ignore
            const morphSection = window.morphSection || window.Theme.morphSection;
            try {
              morphSection(sectionId, parsedResponse.sections[sectionId]);
            } catch (error) {
              console.warn('[BV Cart] Error morphing section:', error);
              // Fallback to innerHTML update
              const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
              if (sectionElement && parsedResponse.sections[sectionId]) {
                sectionElement.innerHTML = parsedResponse.sections[sectionId];
              }
            }
          } else {
            // Fallback: use innerHTML update
            const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
            if (sectionElement && parsedResponse.sections[sectionId]) {
              sectionElement.innerHTML = parsedResponse.sections[sectionId];
            }
          }
        }

        // Dispatch cart update event
        // @ts-ignore
        const CartUpdateEvent = window.CartUpdateEvent || (window.Theme && window.Theme.events && window.Theme.events.CartUpdateEvent);
        // @ts-ignore
        const ThemeEvents = window.ThemeEvents || (window.Theme && window.Theme.events);
        
        if (CartUpdateEvent) {
          document.dispatchEvent(
            new CartUpdateEvent(parsedResponse, sectionId, {
              source: 'bv-draft-product-removal',
              sections: parsedResponse.sections,
            })
          );
        } else if (ThemeEvents && ThemeEvents.cartUpdate) {
          document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, {
            detail: {
              data: parsedResponse,
              source: 'bv-draft-product-removal',
              sections: parsedResponse.sections,
            }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('cart:update', {
            detail: {
              data: parsedResponse,
              source: 'bv-draft-product-removal',
              sections: parsedResponse.sections,
            }
          }));
        }

        // Small delay between removals to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('[BV Cart] Error removing draft product at line', line, ':', error);
      }
    }
  }

  // Initialize draft product removal on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      setTimeout(removeDraftProductsFromCart, 500);
    });
  } else {
    setTimeout(removeDraftProductsFromCart, 500);
  }

  /* ===============================
     REBUY CART SYNC (MINI CART)
  ============================== */

  function getCartForExternalUpdate() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then((response) => response.json());
  }

  function getSectionHtmlForExternalUpdate(sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    return fetch(url.toString(), { credentials: 'same-origin' }).then((response) => response.text());
  }

  function refreshCartAfterExternalAdd(source = 'rebuy') {
    return getCartForExternalUpdate()
      .then((cart) => {
        const itemCount = Number(cart && cart.item_count) || 0;
        let sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((element) => element.getAttribute('data-section-id'))
          .filter(Boolean);

        sectionIds = sectionIds.filter((id, index, arr) => arr.indexOf(id) === index);

        const sections = {};

        return Promise.all(
          sectionIds.map((id) =>
            getSectionHtmlForExternalUpdate(id).then((html) => {
              sections[id] = html;
            })
          )
        ).then(() => {
          document.dispatchEvent(new CustomEvent('cart:update', {
            detail: {
              data: { itemCount, source, sections },
              cart
            }
          }));

          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

          const drawer = document.querySelector('cart-drawer-component');
          if (drawer && typeof /** @type {any} */ (drawer).open === 'function') {
            /** @type {any} */ (drawer).open();
          }
        });
      })
      .catch((error) => {
        console.warn('[BV Mini Cart] External cart refresh failed:', error);
      });
  }

  document.addEventListener('rebuy.add', () => {
    refreshCartAfterExternalAdd('rebuy');
  });

  /* ===============================
     MINI CART BUNDLE COMPONENT REMOVE
  ================================= */

  /**
   * @param {Event} event
   */
  async function handleBundleComponentRemove(event) {
    const target = event.target instanceof Element ? event.target : null;
    const btn = target?.closest('.mini-cart-bundle__component-remove-btn');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    const card = btn.closest('.mini-cart-bundle-card');
    if (!card) return;

    const compEl = btn.closest('.mini-cart-bundle__component');
    const compIndex = parseInt(btn.getAttribute('data-component-index') || (compEl ? (compEl.getAttribute('data-component-index') || '0') : '0'), 10);
    const lineIndex = parseInt(card.getAttribute('data-line-index') || card.dataset.lineIndex || '0', 10);
    const itemKey = card.getAttribute('data-key') || card.dataset.key || '';

    if (!lineIndex) return;

    card.classList.add('mini-cart-bundle-card--updating');

    try {
      const currentCartRes = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const currentCart = await currentCartRes.json();
      const currentLineItem = (currentCart.items || []).find((it) => it.key === itemKey) || (currentCart.items || [])[lineIndex - 1];

      let properties = Object.assign({}, currentLineItem?.properties || {});
      const sectionIds = getCartSectionIds();

      if (properties._group) {
        let group = [];
        try {
          group = typeof properties._group === 'string' ? JSON.parse(properties._group) : properties._group;
        } catch (e) {
          console.warn('[MiniCartBundle] Failed to parse _group:', e);
        }

        if (Array.isArray(group) && group.length > 0) {
          if (compIndex < group.length) {
            group.splice(compIndex, 1);
          } else {
            group.pop();
          }

          if (group.length === 0) {
            await fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ line: lineIndex, quantity: 0, sections: sectionIds.join(','), sections_url: window.location.pathname }),
            });
          } else if (group.length === 1) {
            const remaining = group[0];
            const remainingVariantId = parseInt(remaining.id, 10);

            await fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ line: lineIndex, quantity: 0 }),
            });

            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: remainingVariantId, quantity: 1 }],
                sections: sectionIds.join(','),
                sections_url: window.location.pathname,
              }),
            });
            await addRes.json();
          } else {
            if (!group.some((g) => g.is_main)) {
              group[0].is_main = true;
            }
            const mainItem = group.find((g) => g.is_main) || group[0];
            const primaryVariantId = parseInt(mainItem.id, 10);

            properties._group = JSON.stringify(group);

            await fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ line: lineIndex, quantity: 0 }),
            });

            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: primaryVariantId, quantity: 1, properties }],
                sections: sectionIds.join(','),
                sections_url: window.location.pathname,
              }),
            });
            await addRes.json();
          }
        }
      } else {
        await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: 0, sections: sectionIds.join(','), sections_url: window.location.pathname }),
        });
      }

      const updatedCartRes = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const updatedCart = await updatedCartRes.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          detail: {
            data: {
              itemCount: Number(updatedCart.item_count) || 0,
              source: 'bundle-component-remove',
            },
            cart: updatedCart,
          },
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: updatedCart },
        })
      );
    } catch (err) {
      console.error('[MiniCartBundle] Error removing bundle component:', err);
      refreshCartDrawerPricesOnOpen();
    } finally {
      card.classList.remove('mini-cart-bundle-card--updating');
    }
  }

  /* ===============================
     MINI CART BUNDLE COMPONENT QUANTITY CHANGE
  ================================= */
  /**
   * @param {Event} event
   */
  async function handleBundleComponentQuantityChange(event) {
    const target = event.target instanceof Element ? event.target : null;
    const bundleComp = target?.closest('.mini-cart-bundle__component');
    if (!bundleComp) return;

    // Stop default whole-bundle line item update in CartItemsComponent
    event.stopImmediatePropagation();
    event.preventDefault();

    const card = bundleComp.closest('.mini-cart-bundle-card');
    if (!card) return;

    const lineIndex = parseInt(card.getAttribute('data-line-index') || card.dataset.lineIndex || '0', 10);
    const itemKey = card.getAttribute('data-key') || card.dataset.key || '';
    const compVariantId = parseInt(bundleComp.getAttribute('data-component-variant-id') || bundleComp.dataset.componentVariantId || '0', 10);

    // @ts-ignore
    const newQty = event.detail?.quantity;

    if (!lineIndex || !compVariantId || typeof newQty !== 'number') return;

    card.classList.add('mini-cart-bundle-card--updating');

    try {
      const currentCartRes = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const currentCart = await currentCartRes.json();
      const currentItems = currentCart.items || [];
      const currentLineItem = currentItems.find((it) => it.key === itemKey) || currentItems[lineIndex - 1];

      if (!currentLineItem) return;

      const bundleQty = currentLineItem.quantity || 1;
      const delta = newQty - bundleQty;
      if (delta === 0) return;

      const allBundleCompEls = Array.from(card.querySelectorAll('.mini-cart-bundle__component'));
      const allCompVariantIds = allBundleCompEls.map((el) =>
        parseInt(el.getAttribute('data-component-variant-id') || el.dataset.componentVariantId || '0', 10)
      ).filter(Boolean);

      const otherCompVariantIds = allCompVariantIds.filter((id) => id !== compVariantId);

      const getStandaloneItems = (variantId) => {
        return currentItems.filter((it) => {
          if (it.variant_id !== variantId) return false;
          if (it.item_components && it.item_components.length > 0) return false;
          if (it.properties && it.properties._group) return false;
          return true;
        });
      };

      if (delta > 0) {
        const canFormFullBundles = otherCompVariantIds.length > 0 && otherCompVariantIds.every((otherId) => {
          const standaloneItems = getStandaloneItems(otherId);
          const totalStandaloneQty = standaloneItems.reduce((sum, it) => sum + (it.quantity || 0), 0);
          return totalStandaloneQty >= delta;
        });

        if (canFormFullBundles) {
          const updates = {};
          updates[currentLineItem.key] = bundleQty + delta;

          for (const otherId of otherCompVariantIds) {
            let needed = delta;
            const standaloneItems = getStandaloneItems(otherId);
            for (const it of standaloneItems) {
              if (needed <= 0) break;
              const deduct = Math.min(it.quantity, needed);
              updates[it.key] = it.quantity - deduct;
              needed -= deduct;
            }
          }

          await fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: compVariantId, quantity: delta }],
            }),
          });
        }
      } else {
        const reduceAmount = Math.abs(delta);
        const standaloneItems = getStandaloneItems(compVariantId);
        const totalStandaloneQty = standaloneItems.reduce((sum, it) => sum + (it.quantity || 0), 0);

        if (totalStandaloneQty >= reduceAmount) {
          const updates = {};
          let needed = reduceAmount;
          for (const it of standaloneItems) {
            if (needed <= 0) break;
            const deduct = Math.min(it.quantity, needed);
            updates[it.key] = it.quantity - deduct;
            needed -= deduct;
          }

          await fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });
        } else if (bundleQty > reduceAmount) {
          const updates = {};
          updates[currentLineItem.key] = bundleQty - reduceAmount;

          await fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });

          const itemsToAdd = otherCompVariantIds.map((id) => ({
            id: id,
            quantity: reduceAmount,
          }));

          if (itemsToAdd.length > 0) {
            await fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: itemsToAdd }),
            });
          }
        } else {
          const removeBtn = bundleComp.querySelector('.mini-cart-bundle__component-remove-btn');
          if (removeBtn && removeBtn instanceof HTMLElement) {
            removeBtn.click();
            return;
          }
        }
      }

      await refreshCartAfterExternalAdd('bundle-component-quantity');
    } catch (err) {
      console.error('[MiniCartBundle] Error updating bundle component quantity:', err);
      refreshCartDrawerPricesOnOpen();
    } finally {
      card.classList.remove('mini-cart-bundle-card--updating');
    }
  }

  // Bubble phase on purpose: the remove button must not be swallowed before its own target handlers.
  document.addEventListener('click', handleBundleComponentRemove);
  document.addEventListener('quantity-selector:update', handleBundleComponentQuantityChange, true);

  /* ===============================
     CONSOLIDATED CART EVENT LISTENERS
     Previously each of these events had 2-5 separate document listeners spread through the
     file. They are now one listener per event. Handlers run in the SAME order as before, and
     each is isolated (runIsolated) so one failing cannot stop the others, exactly like the
     separate listeners behaved.

     Image fallback and draft-product removal are debounced with the SAME delays as before
     (200ms / 300ms). A burst such as drawer open -> price refresh -> cart:update now runs
     each of them once, after the last event, instead of 2-3 times. The end result is the same.
  ============================== */

  const scheduleImageHandlers = debounce(initCartImageErrorHandlers, 200);
  const scheduleDraftRemoval = debounce(removeDraftProductsFromCart, 300);

  document.addEventListener("cart-drawer:open", () => {
    // Always reset to Cart tab when drawer opens to prevent flicker from the last active tab.
    runIsolated(resetMiniCartTabs);
    runIsolated(refreshCartDrawerPricesOnOpen);
    runIsolated(applyDeviceFingerpintToCart);
    scheduleImageHandlers();
    scheduleDraftRemoval();
  });

  document.addEventListener("cart:update", () => {
    runIsolated(() => {
      document.querySelectorAll("cart-drawer-component").forEach((drawer) => {
        if (drawer instanceof HTMLElement) maybeHideCheckoutError(drawer);
      });
    });

    // Update button states for all quantity selectors in mini cart
    requestAnimationFrame(() => {
      setTimeout(updateDrawerQuantityButtonStates, 100);
    });

    scheduleImageHandlers();
    scheduleDraftRemoval();
  });

  document.addEventListener("cart:refresh", () => {
    scheduleImageHandlers();
    scheduleDraftRemoval();
  });

  // Fired by the Shopify theme editor only.
  document.addEventListener("shopify:section:load", (e) => {
    runIsolated(bootTabs);

    if (e instanceof CustomEvent && e.detail && e.detail.sectionId && e.detail.sectionId.includes('cart')) {
      setTimeout(() => {
        setupCartImageObserver();
        initCartImageErrorHandlers();
      }, 100);
      scheduleDraftRemoval();
    }
  });
})();
